import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import type { SmileyObject } from './smiley.interface.js';
import { DEFAULT_SMILEY_ICON, type SmileyIcon } from './smiley-icons.config.js';

const MIN_DRAG_SIDE_PX = 20;

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the SmileyObject contract.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Build a fresh smiley default payload — extracted from the inline
 * `placeSmileyAt` body in `o2d-whiteboard.element.ts` per PRD-F3 §TR1 / §TR4.
 *
 * - `sizeOpt === null`: bare-click branch, smiley is centred on the pointer
 *   (top-left = point - size/2). Dimensions follow `creationDefaults.smileySize`
 *   which scales inversely with viewport scale.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer). Each side is clamped to a
 *   minimum of 20px (legacy `Math.max(size.*, 20)` parity).
 */
export function createSmileyDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  icon: SmileyIcon = DEFAULT_SMILEY_ICON,
  // Stefan, 2026-09-03 — "choose properties before drawing". Bugreport
  // (Anke): "beim... icon ist fast nichts korrekt angeschlossen" — `fill`
  // (SmileyObject's motif recolour) had no override plumbing at all. No
  // hardcoded default (unset = motif keeps its original colours), so a
  // conditional-spread rather than a plain assignment.
  fill?: string,
): SmileyObject {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? Math.max(sizeOpt.width, MIN_DRAG_SIDE_PX) : cd.smileySize;
  const h = sizeOpt ? Math.max(sizeOpt.height, MIN_DRAG_SIDE_PX) : cd.smileySize;
  const x = sizeOpt ? point.x : point.x - cd.smileySize / 2;
  const y = sizeOpt ? point.y : point.y - cd.smileySize / 2;
  const smiley: SmileyObject = {
    id: generateShortId(),
    type: 'smiley',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    icon,
    ...(fill !== undefined ? { fill } : {}),
  };
  return freezeInDev(smiley);
}
